import {
    Component,
    createEffect,
    createMemo,
    createSignal,
    For,
    on,
    onCleanup,
    onMount,
    Setter,
    Show,
} from 'solid-js'

import { A, useNavigate, useSearchParams } from '@solidjs/router'
import {
    api_admin_helmet_extensions_count_get,
    api_admin_helmet_extensions_del,
    api_admin_helmet_extensions_list,
    api_admin_helmet_extensions_patch,
    api_admin_helmet_extensions_post,
    api_admin_helmets_bulk_search_post,
    Count,
    HelmetExtension,
} from 'abi'
import {
    addAlert,
    Counter,
    HoverInp,
    LoadingDots,
    Pagination,
    setPopup,
} from 'components'
import { AddBtn } from 'components/addBtn'
import {
    ArrowDownIcon,
    ArrowLeftIcon,
    CheckboxFill,
    CheckboxUnFill,
    CheckFillIcon,
    CloseIcon,
    DeleteIcon,
    EditIcon,
    PrintIcon,
    SelectAllIcon,
    SettingFillIcon,
    WarningIcon,
} from 'icons'
import readXlsxFile, { Row } from 'read-excel-file'
import { createStore, produce, unwrap } from 'solid-js/store'
import { deepcopy, objentries } from 'utils/tools'
import { DEFAULT_ECONOMIC_NUMBER, DEFAULT_PHONE, outletRef } from '..'
import {
    ACCEPT_STRING,
    EXCEL_EXTENSIONS,
    EXCEL_MIMES,
    ExcelLoading,
    ExcelStatus,
} from '../insurances/addIns'
import { PrintHelmetExt } from '../insurances/print'
import { isAdmin } from '../users/corp/shared'
import SelectedExtentions from './select'
import './style/submitteds.scss'

export const FORMATS: (keyof HelmetExtension)[] = ['total_insurance_amount']

export const HELMET_EXTENSION_DEF: HelmetExtension = {
    helmet: null,
    airrm_cover_passenger: 0,
    issue_date: '',
    issuer: '',
    ext_number: 0,
    created_at: 0,
    transaction_kind: '',
    ext_issue_date: '',
    passenger_count: 0,
    total_insurance_amount: 0,
    expiration_date: '',
    plate_number: '',
    v_name: '',
    insurance_number: 0,
    ext_row: 0,
    life_surplus_insurance_amount: 0,
    vin: '',
    apply_date: '',
    issue_year: 0,
    plate_city: '',
    reagent: '',
    insurer_phone: '',
    manufacture_year: 0,
    unique_code: '',
    v_usage: '',
    ext_change: '',
    engine: '',
    airrm_cover_life: 0,
    insurance_code: 0,
    insurer_code: '',
    issue_time: 0,
    transaction_detail: '',
    print_count: 0,
    transaction_number: 0,
    transaction_summary: '',
    v_group: '',
    ext_code: 0,
    passenger_insurance_amount: 0,
    past_insurer: '',
    financial_surplus_insurance_amount: 0,
    past_expiration_date: '',
    added_toll: 0,
    start_date: '',
    third_party_insurance_amount: 0,
    v_system: '',
    v_color: '',
    added_tax: 0,
    airrm_cover_financial: 0,
    fund_fine: 0,
    chassis: '',
    id: 0,
    insurer: '',
    plate_serial: '',
    economic_number: '',
    issuer_code: 0,
}
type ImportsT =
    | 'id'
    | 'company'
    | 'cargo'
    | 'created_at'
    | 'print_count'
    | 'insurer_phone'
    | 'passenger_count'
    | 'provider'
    | 'issue_year'
    | 'insurer_code'
    | 'helmet'
type HelmetImport = Omit<HelmetExtension, ImportsT>

export const HEL_EXT_COL_MAP: {
    [k in keyof HelmetImport]: { cx: number; cn: string[] }
} = {
    ext_change: { cx: -1, cn: ['نوع تغییر'] },
    ext_number: { cx: -1, cn: ['شماره الحاقیه'] },
    ext_row: { cx: -1, cn: ['ردیف'] },
    transaction_number: { cx: -1, cn: ['شماره تراکنش'] },
    transaction_detail: { cx: -1, cn: ['شرح تراکنش'] },
    transaction_summary: { cx: -1, cn: ['خلاصه تراکنش'] },
    transaction_kind: { cx: -1, cn: ['نوع تراکنش'] },
    apply_date: { cx: -1, cn: ['تاریخ اعمال'] },
    ext_code: { cx: -1, cn: ['کد رایانه نسخه'] },
    ext_issue_date: { cx: -1, cn: ['تاریخ صدور'] },
    economic_number: { cx: 10, cn: ['شماره اقتصادی کسب و کار'] },
    issuer_code: { cx: 83, cn: ['کد نمایندگی تحویل'] },
    unique_code: { cx: 181, cn: ['کد یکتای پیشنهاد'] },
    insurance_number: { cx: 4, cn: ['شماره بیمه نامه'] },
    insurance_code: { cx: 1, cn: ['کد رایانه بیمه نامه'] },
    insurer: { cx: 7, cn: ['بیمه گذار'] },
    v_color: { cx: 82, cn: ['رنگ خودرو', 'ColorText'] },
    chassis: { cx: 28, cn: ['شماره شاسی'] },
    engine: { cx: 27, cn: ['شماره موتور'] },
    plate_serial: { cx: 21, cn: ['سریال پلاک'] },
    plate_number: { cx: 19, cn: ['شماره پلاک'] },
    plate_city: { cx: 20, cn: ['شهر پلاک'] },
    vin: { cx: 29, cn: ['VIN'] },
    v_system: { cx: 106, cn: ['سیستم خودرو'] },
    v_group: { cx: 105, cn: ['نام گروه خودرو'] },
    v_name: { cx: 22, cn: ['نوع خودرو'] },
    v_usage: { cx: 25, cn: ['مورد استفاده خودرو'] },
    manufacture_year: { cx: 26, cn: ['سال ساخت خودرو'] },
    total_insurance_amount: { cx: 62, cn: ['كل حق بیمه', 'حق بیمه با مالیات و عوارض'] },

    issuer: { cx: 11, cn: ['صادر کننده'] },
    issue_date: { cx: 137, cn: ['تاریخ صدور بیمه نامه'] },
    reagent: { cx: 12, cn: ['معرف'] },
    expiration_date: { cx: 14, cn: ['تاریخ انقضاء', 'تاریخ انقضاء تا ساعت 24 روز'] },
    start_date: { cx: 13, cn: ['تاریخ شروع', 'تاریخ شروع از ساعت 24 روز'] },
    past_insurer: { cx: 40, cn: ['شرکت بیمه سال قبل'] },
    past_expiration_date: {
        cx: 45,
        cn: ['تاریخ انقضاء بیمه نامه سال قبل'],
    },
    added_tax: { cx: 63, cn: ['مالیات ارزش افزوده'] },
    added_toll: { cx: 64, cn: ['عوارض ارزش افزوده'] },

    airrm_cover_life: { cx: 47, cn: ['پوشش جانی (میلیون ریال)', 'پوشش بدنی (میلیون ریال)'] },
    airrm_cover_financial: { cx: 48, cn: ['پوشش مالی (میلیون ریال)'] },
    airrm_cover_passenger: {
        cx: 49,
        cn: ['پوشش حوادث راننده (میلیون ریال)', 'پوشش سرنشین (میلیون ریال)'],
    },
    issue_time: { cx: 152, cn: ['زمان ثبت'] },
    // unique_code: { cx: 77, cn: ['کد یکتای بیمه‌ نامه'] },

    third_party_insurance_amount: {
        cx: 52,
        cn: ['حق بیمه ثالث اجباری نهایی'],
    },
    passenger_insurance_amount: {
        cx: 53,
        cn: ['حق بیمه سرنشین نهایی', 'حق بیمه حوادث راننده نهایی'],
    },
    life_surplus_insurance_amount: {
        cx: 54,
        cn: ['حق بیمه مازاد جانی نهایی', 'حق بیمه مازاد بدنی نهایی'],
    },
    financial_surplus_insurance_amount: {
        cx: 55,
        cn: ['حق بیمه مازاد مالی نهایی'],
    },
    fund_fine: { cx: 56, cn: ['صندوق'] },
    // helmet: { cx: -1, cn: ['شماره بیمه'] },
}

const MANUALS = [
    'issue_year',
    'insurer_code',
    'insurer_phone',
    'passenger_count',
    'helmet',
] as const

type ManualKey = (typeof MANUALS)[number]
export const EXT_MANUALS_LABELS: Record<ManualKey, string> = {
    helmet: 'شماره بیمه',
    issue_year: 'سال صدور',
    insurer_code: 'کد بیمه‌گذار',
    insurer_phone: 'شماره تلفن بیمه‌گذار',
    passenger_count: 'تعداد مسافران',
}

type STATE = {
    count: Count

    page: number

    anim: boolean

    query: string

    loadingC: boolean
    loadingI: boolean

    extentions: HelmetExtension[]
    extenttionPrint: HelmetExtension | null

    stat: [number, number]

    add: {
        type: 'edit' | 'add'
        show: boolean
        ins: HelmetExtension | null
    }
}

const [state, setState] = createStore<STATE>({
    anim: false,

    loadingI: true,
    loadingC: true,

    query: '',
    extentions: [],

    page: 0,
    count: {
        max_page: -1,
        total: -1,
    },

    extenttionPrint: null,

    stat: [-1, -1],

    add: {
        show: false,
        type: 'add',
        ins: null,
    },
})

export { state as extentionState }

const ExtentionSubmitteds: Component = () => {
    let nav = useNavigate()

    const [search_params, setSearchParams] = useSearchParams()

    onMount(() => {
        if (!isAdmin()) return nav('/panel/403', { replace: true })

        let p = parseInt(search_params.page || '0')
        if (p !== 0) {
            setState('page', p)
        }

        let page = unwrap(state.page)
        setSearchParams({ page })

        let ac = new AbortController()

        getCount()
        searchExtentions()

        window.addEventListener('popstate', handlePopstate, {
            signal: ac.signal,
        })

        onCleanup(() => ac.abort())
    })

    const getCount = async () => {
        setState({ loadingC: true })
        let res = await api_admin_helmet_extensions_count_get({})
        setState({ loadingC: false })

        if (res.ok()) {
            setState({ count: res.body })
        }
    }

    const searchExtentions = async () => {
        let s = deepcopy(state)

        let page = s.page

        let result: HelmetExtension[] = []

        setState('loadingI', true)

        let res = await api_admin_helmet_extensions_list({ page })
        setState('loadingI', false)

        if (!res.ok()) return

        result = res.body

        setState('extentions', result)
    }

    const handlePopstate = () => {
        let page = parseInt(search_params.page || '0')

        doSearch(page)
    }

    const doSearch = (page: number) => {
        setSearchParams({ page })
        setState({ page })
        searchExtentions()

        outletRef?.scrollTo({
            top: 0,
            behavior: 'smooth',
        })
    }

    return (
        <div class='submitteds-section section'>
            <div class='submitteds-wrapper'>
                <A href='/panel/extentions' class='extention-go-back'>
                    <ArrowLeftIcon />
                </A>
                <div class='head section_title'>
                    <EditIcon />
                    <span>الحاقیه ها</span>
                    <EditIcon />
                </div>
                <div
                    class='total-wrapper title_small'
                    classList={{ active: !state.loadingC }}
                >
                    <Counter end={state.count.total} />
                    <span>الحاقیه ثبت شده</span>
                </div>
                <AddBtn
                    onClick={() => {
                        setState('add', {
                            show: true,
                            ins: null,
                            type: 'add',
                        })
                    }}
                    class='title_smaller'
                    title='اضافه کردن'
                />
                <div
                    class='table-container'
                    classList={{
                        min: state.extentions?.length == 0,
                        loading: state.loadingI,
                    }}
                >
                    <table>
                        <thead>
                            <tr class='title_small'>
                                <th class='index'>
                                    <span>ردیف</span>
                                </th>
                                <th class='ins'>
                                    <span>بیمه صدور</span>
                                </th>
                                <th>
                                    <span>VIN</span>
                                </th>
                                <th>
                                    <span>موتور</span>
                                </th>
                                <th>
                                    <span>سریال پلاک</span>
                                </th>
                                <th>
                                    <span>شماره پلاک</span>
                                </th>
                                <th>
                                    <span>تاریخ صدور</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <Show when={!state.loadingI} fallback={<Loading />}>
                                <Show
                                    when={state.extentions.length > 0}
                                    fallback={
                                        <tr class='table-empty title_hero'>
                                            <td colspan='10'>
                                                <div class='icon'>
                                                    <CloseIcon />
                                                </div>
                                                <div>
                                                    الحاقیه ای برای نمایش وجود
                                                    ندارد!
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                >
                                    <For each={state.extentions}>
                                        {c => (
                                            <HelmetTableRow
                                                ins={c}
                                                onClick={() => {
                                                    setState('add', {
                                                        ins: c,
                                                        show: true,
                                                        type: 'edit',
                                                    })
                                                }}
                                            />
                                        )}
                                    </For>
                                </Show>
                            </Show>
                        </tbody>
                    </table>
                </div>
                <Show when={state.count.max_page > 0}>
                    <Pagination
                        max_page={state.count.max_page}
                        current={state.page || 0}
                        onChange={page => {
                            doSearch(page)
                        }}
                    />
                </Show>
            </div>

            <AddExtentionCmp
                onClose={() =>
                    setState('add', { type: 'add', ins: null, show: false })
                }
                add={state.add}
                onDel={c => {
                    setState(
                        produce(s => {
                            let id = s.extentions.findIndex(a => a.id == c.id)
                            if (id == -1) return

                            s.extentions.splice(id, 1)
                        })
                    )
                }}
                onAdd={c => {
                    {
                        setState(
                            produce(s => {
                                s.extentions.push(...c)
                                s.count.total += c.length
                            })
                        )
                    }
                }}
                onEdit={c => {
                    {
                        setState(
                            produce(s => {
                                let id = s.extentions.findIndex(
                                    a => a.id == c.id
                                )
                                if (id == -1) return

                                s.extentions[id] = deepcopy(c)
                            })
                        )
                    }
                }}
                onExtentionPrint={c => {
                    setState(
                        produce(s => {
                            s.extenttionPrint = c
                        })
                    )
                }}
            />

            <Show when={state.extenttionPrint}>
                <PrintHelmetExt
                    onPrint={() => {}}
                    helmetExt={state.extenttionPrint!}
                    onClose={() => setState('extenttionPrint', null)}
                />
            </Show>
        </div>
    )
}

type TableRowProps = {
    ins: HelmetExtension
    onClick(): void
}
export const HelmetTableRow: Component<TableRowProps> = P => {
    return (
        <tr class='title_smaller' onclick={P.onClick}>
            <td class='index'>{P.ins.id}</td>
            <td>{P.ins.helmet || <span class='empty'>خالی!</span>}</td>
            <td class='vin'>{P.ins.vin || <span class='empty'>خالی!</span>}</td>
            <td>{P.ins.engine || <span class='empty'>خالی!</span>}</td>
            <td>{P.ins.plate_serial || <span class='empty'>خالی!</span>}</td>
            <td>{P.ins.plate_number || <span class='empty'>خالی!</span>}</td>
            <td class='date'>{P.ins.issue_date}</td>
        </tr>
    )
}

interface addExtProps {
    add: STATE['add']

    onClose(): void

    onAdd(c: HelmetExtension[]): void
    onDel(c: HelmetExtension): void
    onEdit(c: HelmetExtension): void
    onExtentionPrint(c: HelmetExtension): void
}

type DATA = {
    loading: boolean
    status: null | 'green' | 'red'

    sec: 'file' | 'manual'

    anim: boolean

    loadingXls: string
    rows: Row[]
    // cols: number
    // view_cols: CellValue[][]
    helmets: HelmetExtension[]

    stat: [number, number]

    select: {
        show: boolean
        selected: number[]
    }
}
const [data, setData] = createStore<DATA>({
    loading: false,
    status: null,

    sec: 'file',

    anim: false,

    loadingXls: '',
    rows: [],
    // cols: 0,
    helmets: [],
    // get view_cols() {
    //     let out: CellValue[][] = []
    //     for (let cdx = 0; cdx < this.cols; cdx++) {
    //         let col: CellValue[] = []
    //         for (let r of this.rows) {
    //             if (r[cdx] == undefined) break
    //             col.push(r[cdx]!)
    //         }
    //         out.push(col)
    //     }
    //     return out
    // },

    stat: [-1, -1],

    select: {
        show: false,
        selected: [],
    },
})

export const AddExtentionCmp: Component<addExtProps> = P => {
    createEffect(
        on(
            () => P.add.show,
            s => {
                if (!s) return closed()

                if (P.add.type == 'edit') {
                    setData({
                        loading: false,
                        loadingXls: '',
                        status: null,

                        sec: 'manual',

                        helmets: [deepcopy(P.add.ins!)],
                    })
                    return
                }

                // similar to init
                closed()
            }
        )
    )

    const closed = () => {
        setData({
            loading: false,
            status: null,

            sec: 'file',

            anim: false,

            loadingXls: '',
            rows: [],
            // cols: 0,
            helmets: [],
        })
    }

    type COMPARE_T = Omit<HelmetExtension, 'id'>
    const keys: (keyof COMPARE_T)[] = [
        'helmet',
        'issuer',
        'passenger_count',
        'third_party_insurance_amount',
        'plate_number',
        'unique_code',
        'issue_date',
        'created_at',
        'insurer_phone',
        'v_color',
        'transaction_number',
        'v_name',
        'chassis',
        'v_usage',
        'issue_time',
        'start_date',
        'life_surplus_insurance_amount',
        'ext_code',
        'engine',
        'v_system',
        'ext_number',
        'ext_row',
        'passenger_insurance_amount',
        'insurer_code',
        'transaction_kind',
        'fund_fine',
        'insurance_number',
        'issuer_code',
        'print_count',
        'total_insurance_amount',
        'v_group',
        'ext_issue_date',
        'expiration_date',
        'added_tax',
        'plate_serial',
        'issue_year',
        'airrm_cover_passenger',
        'airrm_cover_financial',
        'apply_date',
        'manufacture_year',
        'plate_city',
        'transaction_detail',
        'ext_change',
        'financial_surplus_insurance_amount',
        'insurance_code',
        'insurer',
        'transaction_summary',
        'airrm_cover_life',
        'vin',
        'past_insurer',
        'added_toll',
        'past_expiration_date',
        'economic_number',
        'reagent',
    ]
    const changed = createMemo(() => {
        if (P.add.type == 'add') {
            let change_xls = data.rows.length !== 0
            let change_manu = data.helmets.length !== 0

            return change_xls || change_manu
        } else {
            const h = data.helmets[0] as Partial<COMPARE_T> | undefined
            const p = P.add.ins as Partial<COMPARE_T> | undefined

            if (!h && !p) return false

            if (!h || !p) return true

            return keys.some(k => {
                const a = h[k]
                const b = p[k]

                if (a == null && b == null) return false

                return String(a ?? '') !== String(b ?? '')
            })
        }
    })

    const patchHelmet = async () => {
        if (data.loading || data.loadingXls || data.status) return

        if (!changed()) {
            addAlert({
                type: 'error',
                timeout: 3,
                subject: 'مطلبی را عوض نکردید!',
            })
            return
        }

        let id = P.add.ins?.id

        if (!id) return

        let h = deepcopy(data.helmets[0]!)

        setData({ loading: true })

        const payload: HelmetExtension = { ...h }

        let res = await api_admin_helmet_extensions_patch(
            { id },
            payload as any
        )
        setData({ loading: false })

        if (!res.ok()) return setData('status', 'red')

        setData('status', 'green')
        pop_conffeties()
        P.onEdit(res.body)
    }
    const addHelmets = async () => {
        if (data.loading || data.loadingXls || data.status) return

        if (!changed()) {
            addAlert({
                type: 'error',
                timeout: 3,
                subject: 'مطلبی را عوض نکردید!',
            })
            return
        }

        if (data.helmets.length == 0) {
            addAlert({
                type: 'error',
                timeout: 3,
                subject: 'خطا!',
                content: 'در فایل آپلودی، اطلاعات بیمه‌ای یافت نشد!',
            })
            return
        }

        setData({ loading: true })

        setData(
            produce(s => {
                s.stat = [0, data.helmets.length]
            })
        )

        const N = 50
        let bmax = data.helmets.length / N + 2
        for (let batch = 0; batch <= bmax; batch++) {
            let hb = data.helmets.slice(batch * N, (batch + 1) * N)
            if (hb.length == 0) break
            let up = hb.map(async (h, id) => {
                // let payload: Helmet = { ...h }

                let res = await api_admin_helmet_extensions_post(h)
                if (!res.ok()) {
                    addAlert({
                        type: 'error',
                        subject: `خطا در ردیف ${id}`,
                        content: res.body.debug || '',
                        timeout: 3,
                    })
                    throw new Error('')
                }

                setData(
                    produce(s => {
                        s.helmets[id]!['id'] = res.body.id
                        s.stat[0] = s.stat[0] + 1
                    })
                )
            })

            await Promise.all(up)
        }

        location.reload()

        setData('loading', false)
    }

    const delIns = async () => {
        if (data.loading || data.loadingXls || !!data.status) return

        let id = P.add.ins?.id

        if (!id) return

        setData({ loading: true })

        let res = await api_admin_helmet_extensions_del({ id })

        setData({ loading: false })

        if (!res.ok()) return setData('status', 'red')
        setData('status', 'green')
        P.onDel(P.add.ins!)
        return
    }

    const pop_conffeties = () => {
        // @ts-ignore
        if (typeof window.confetti !== 'function') return

        const count = 200,
            defaults = {
                origin: { y: 0.7 },
            }

        function fire(particleRatio: number, opts: any) {
            // @ts-ignore
            window.confetti(
                Object.assign({}, defaults, opts, {
                    particleCount: Math.floor(count * particleRatio),
                })
            )
        }

        fire(0.25, {
            spread: 26,
            startVelocity: 55,
        })

        fire(0.2, {
            spread: 60,
        })

        fire(0.35, {
            spread: 100,
            decay: 0.91,
            scalar: 0.8,
        })

        fire(0.1, {
            spread: 120,
            startVelocity: 25,
            decay: 0.92,
            scalar: 1.2,
        })

        fire(0.1, {
            spread: 120,
            startVelocity: 45,
        })
    }

    const OnClose = () => {
        if (data.status == 'red')
            return setData({ loading: false, status: null })

        if (data.loading) return

        if (changed() && !data.status) {
            setPopup({
                show: true,
                title: 'ذخیره نکردید!',
                content: 'بعد از خروج، تغییرات شما اعمال نمیشوند!',
                Icon: WarningIcon,
                type: 'warning',
                onSubmit: P.onClose,
            })
            return
        }

        P.onClose()
    }

    return (
        <div
            class='add-extention-cmp'
            classList={{ show: P.add.show }}
            onclick={OnClose}
        >
            <div
                class='add-extention-wrapper'
                onclick={e => {
                    e.stopPropagation()
                }}
            >
                <div
                    class='scaler'
                    onanimationend={() => setData({ anim: true })}
                ></div>

                <button class='close icon' type='button' onclick={OnClose}>
                    <CloseIcon />
                </button>

                <form
                    onsubmit={e => {
                        e.preventDefault()

                        if (data.select.show) return

                        // savePopularPhone()
                        // save_economic_number()

                        if (P.add.type == 'add') {
                            addHelmets()
                        } else {
                            patchHelmet()
                        }
                    }}
                    class='card'
                >
                    <Show when={P.add.type == 'add'}>
                        <div
                            class='sec-picker title_smaller'
                            classList={{ active: data.sec == 'manual' }}
                        >
                            <button
                                type='button'
                                class='file'
                                onclick={() => setData('sec', 'file')}
                            >
                                فایل
                            </button>
                            <button
                                type='button'
                                class='manu'
                                onclick={() => setData('sec', 'manual')}
                            >
                                دستی
                            </button>
                        </div>
                    </Show>

                    <div class='sec-ctas title_smaller'>
                        <button
                            class='sec-cta save'
                            type='submit'
                            classList={{ active: changed() }}
                        >
                            <CheckFillIcon />
                            ذخیره
                        </button>
                        <Show when={P.add.type == 'edit'}>
                            <button
                                class='sec-cta del'
                                onclick={() => {
                                    setPopup({
                                        show: true,
                                        Icon: DeleteIcon,
                                        type: 'error',
                                        title: 'بیمه رو حذف میکنید؟',
                                        content:
                                            'بعد از حذف امکان بازگشت وجود ندارد!',
                                        onSubmit: delIns,
                                    })
                                }}
                                type='button'
                            >
                                <DeleteIcon />
                                حذف
                            </button>
                        </Show>
                        <Show when={!changed()}>
                            <button
                                class='sec-cta print icon'
                                type='button'
                                onclick={() => P.onExtentionPrint(P.add.ins!)}
                            >
                                <PrintIcon />
                            </button>
                        </Show>
                    </div>

                    <div class='secs-container'>
                        <Show when={P.add.type == 'add'}>
                            <FileSec
                                corpPick
                                data={data}
                                setData={setData}
                                add={P.add}
                            />
                        </Show>

                        <ManuSec
                            corpPick
                            data={data}
                            setData={setData}
                            add={P.add}
                        />
                    </div>
                    <Show when={data.select.show}>
                        <SelectedExtentions
                            pickCorp={false}
                            count={data.select.selected.length}
                            show={data.select.show}
                            onClose={() => setData('select', 'show', false)}
                            onSave={h => {
                                let list = unwrap(data.select.selected)

                                list.map(id => {
                                    let idx = data.helmets.findIndex(
                                        hel => hel.id == id
                                    )
                                    if (idx == -1) return

                                    let trues: Partial<HelmetExtension> = {}

                                    for (let [k, v] of objentries(h)) {
                                        if (!v) continue
                                        if (
                                            typeof v == 'string' &&
                                            v.trim().length == 0
                                        )
                                            continue

                                        trues[k] = v as any
                                    }

                                    setData('helmets', idx, prev => ({
                                        ...prev,
                                        ...trues,
                                    }))
                                })
                                setData({
                                    select: {
                                        show: false,
                                        selected: [],
                                    },
                                })
                            }}
                        />
                    </Show>
                </form>

                <ExcelLoading
                    state={data as any}
                    setState={setData as any}
                    add={P.add as any}
                />

                <ExcelStatus
                    state={data as any}
                    setState={setData as any}
                    add={P.add as any}
                />
            </div>
        </div>
    )
}

const Loading = () => {
    return (
        <tr class='table-loading title_hero'>
            <td colspan='10'>
                <img src='/public/imgs/logo.png' decoding='async' alt='' />
                <div>در حال بارگذاری...</div>
            </td>
        </tr>
    )
}

interface SecChildPrps {
    add: STATE['add']

    data: DATA
    setData: Setter<Partial<DATA>>
}

interface FileSecProps extends SecChildPrps {
    corpPick: boolean
}
export const FileSec: Component<FileSecProps> = P => {
    let excelShowerRef: HTMLDivElement

    const Uploader: Component = () => {
        const [dragOver, setDragOver] = createSignal(false)

        onMount(() => {
            let ac = new AbortController()

            document.addEventListener('drop', onDrop, {
                signal: ac.signal,
            })
            document.addEventListener('dragover', onDragOver, {
                signal: ac.signal,
            })

            onCleanup(() => {
                ac.abort()
            })
        })

        function isAcceptedFile(file: File | null) {
            if (!file) return false

            const name = file.name || ''
            const lower = name.toLowerCase()

            // بررسی پسوند
            const hasValidExt = EXCEL_EXTENSIONS.some(ext =>
                lower.endsWith(ext)
            )

            const hasValidMime = !!file.type && EXCEL_MIMES.includes(file.type)

            return hasValidExt || hasValidMime
        }

        async function handleSelectedFile(f?: File) {
            if (!f) return

            if (!isAcceptedFile(f)) {
                addAlert({
                    type: 'error',
                    subject: 'خطا!',
                    timeout: 3,
                    content:
                        'فرمت فایل آپلودی مجاز نیست. لطفاً یک فایل اکسل (.xls, .xlsx, .xlsm) انتخاب کنید.',
                })
                return
            }

            P.setData({ rows: [], loadingXls: 'درحال خواندن فایل اکسل (1/3)' })

            let rows = await readXlsxFile(f)
            // rows = rows.map(r =>
            //     r.map(c => {
            //         if (typeof c == 'string') return c.replaceAll('ي', 'ی')
            //         return c
            //     })
            // )
            // let max = 0
            // for (let r of rows) {
            //     max = Math.max(r.length, max)
            // }

            if (rows.length < 2) {
                return P.setData({
                    rows: [],
                    cols: 0,
                    helmets: [],
                    loadingXls: '',
                })
            }

            let headers = rows[0]!.map(c => {
                if (typeof c == 'string') return c.replaceAll('ي', 'ی')
                return c
            })

            let local_hmap = { ...HEL_EXT_COL_MAP }

            P.setData({ loadingXls: 'درحال پارس کردن سررشته ها (2/3)' })
            await new Promise(r => setTimeout(r, 200))

            for (let [k, v] of objentries(local_hmap)) {
                local_hmap[k].cx = -1

                for (let cn of v.cn) {
                    let ncx = headers.findIndex((l, hix) => l == cn && hix != 0)
                    if (ncx == -1) continue
                    local_hmap[k].cx = ncx
                }

                if (local_hmap[k].cx == -1) {
                    addAlert({
                        type: 'error',
                        subject: 'خطا!',
                        timeout: 3,
                        content: `ستون ${k} پیدا نشد`,
                    })
                    return
                }
            }

            P.setData({
                loadingXls: `درحال تبدیل ${rows.length - 1} بیمه به سیستم همراه (3/3)`,
            })
            await new Promise(r => setTimeout(r, 200))

            let helmets: HelmetExtension[] = []
            let now = performance.now()

            let helmets_insurrers = new Map<
                number,
                { index: number; helmetId: number | null }
            >()

            for (let i = 1; i < rows.length; i++) {
                let r = rows[i]
                if (!r) break
                let h: HelmetExtension = {
                    ...HELMET_EXTENSION_DEF,
                    id: now + i,
                }

                for (let [k, v] of objentries(local_hmap)) {
                    let cv = r[v.cx]
                    // let cv: any = null
                    //
                    // for (let cn of v.cn) {
                    //     let ncx = headers.findIndex(l => l == cn)
                    //     if (ncx == -1) continue
                    //     cv = r[ncx]
                    //     if (cv) break
                    // }

                    // if (cv == null || cv == undefined) continue
                    if (!cv) continue

                    if (typeof h[k] != typeof cv) {
                        addAlert({
                            subject: `نوع نادرست: ${v.cn} | ${typeof h[k]} != ${typeof cv}`,
                            type: 'error',
                            timeout: 5,
                        })
                        continue
                    }

                    if (typeof cv == 'number') cv = ~~cv

                    // @ts-ignore
                    h[k] = cv
                }

                if (DEFAULT_PHONE && !h.insurer_phone) {
                    h.insurer_phone = DEFAULT_PHONE
                }

                if (DEFAULT_ECONOMIC_NUMBER && !h.economic_number) {
                    h.economic_number = DEFAULT_ECONOMIC_NUMBER
                }

                h.issue_year = parseInt(h.issue_date.split('/')[0]!) || -1
                h.insurer_code = h.insurer.split(' کد ')[1] || ''
                h.insurer_code = h.insurer_code.trim()

                helmets_insurrers.set(h.insurance_number, {
                    index: helmets.length,
                    helmetId: null,
                })

                helmets.push(h)
            }

            let entries = Array.from(helmets_insurrers.entries())

            const CHUNK_SIZE = 1000
            for (let i = 0; i < entries.length; i += CHUNK_SIZE) {
                P.setData({
                    loadingXls: `درحال جستجو بیمه‌ها — دسته ${i + 1} از ${Math.round(entries.length / 1000)}`,
                })

                const res = await api_admin_helmets_bulk_search_post({
                    insurance_numbers: entries
                        .slice(i, i + 1000)
                        .map(a => a[0]),
                })

                if (!res.ok()) return

                for (let b of res.body) {
                    helmets_insurrers.set(b.insurance_number, {
                        helmetId: b.id,
                        index: 0,
                    })
                }
            }

            for (let i = 0; i < helmets.length; i++) {
                let h = helmets[i]

                if (!h) continue

                let res = helmets_insurrers.get(h.insurance_number)

                if (!res) continue

                h.helmet = res.helmetId
            }

            P.setData({ rows: rows.slice(0, 20), helmets, loadingXls: '' })
            await new Promise(r => setTimeout(r, 200))

            if (excelShowerRef) {
                excelShowerRef.scrollTo({
                    top: 0,
                    left: excelShowerRef.scrollWidth,
                    behavior: 'instant',
                })
            }

            return
        }

        const onInputChange = (e: Event) => {
            const inp = e.currentTarget as HTMLInputElement
            const f = inp.files
            if (!f || !f[0]) return

            handleSelectedFile(f[0])
            // clear so same file can be picked again later
            inp.value = ''
        }

        const onDrop = (e: DragEvent) => {
            e.preventDefault()
            e.stopPropagation()
            setDragOver(false)

            const dt = e.dataTransfer
            if (!dt) return
            if (!dt.files || dt.files.length === 0) return

            const first = dt.files[0]
            handleSelectedFile(first)
        }

        const onDragOver = (e: DragEvent) => {
            e.preventDefault() // allow drop
            e.dataTransfer!.dropEffect = 'copy'
        }

        const onDragEnter = (e: DragEvent) => {
            e.preventDefault()
            setDragOver(true)
        }

        const onDragLeave = (e: DragEvent) => {
            e.preventDefault()
            setDragOver(false)
        }

        return (
            <label
                class='uploader-cmp'
                for={'hamrah-excel-inp'}
                classList={{
                    active: dragOver(),
                    hide: P.data.rows.length > 2,
                }}
                ondragenter={onDragEnter}
                onDragLeave={onDragLeave}
            >
                <input
                    type='file'
                    id={'hamrah-excel-inp'}
                    accept={ACCEPT_STRING}
                    onChange={onInputChange}
                />
                <img
                    src='/public/imgs/excel.png'
                    decoding='async'
                    loading='lazy'
                    alt=''
                />
                <div class='upload-hints'>
                    <p class='title_small'>فایل اکسل خود را اینجا بندازید</p>
                    <p class='divider title_small'>یا</p>
                    <p class='title_small'>کلیک کنید!</p>
                </div>
            </label>
        )
    }

    const BigPaddings = [
        'معرف',
        'وضعيت',
        'مالک',
        'واحد کاری صدور',
        'واحد کاری معرف',
        'شرح قراداد',
        'آیین نامه',
        'واحد پیشنهاد دهنده',
        'بيمه گذار',
        'شماره قرارداد',
    ]
    const ExcelShower: Component = () => {
        const delFile = () => {
            P.setData({
                loadingXls: '',
                rows: [],
                cols: 0,
                helmets: [],
            })
        }

        return (
            <div
                class='shower-container'
                classList={{ active: P.data.rows.length !== 0 }}
            >
                <button
                    type='button'
                    class='del-file title_smaller'
                    onclick={delFile}
                >
                    <DeleteIcon />
                    حذف فایل
                </button>
                <div class='excel-shower' ref={e => (excelShowerRef = e)}>
                    <table>
                        <thead>
                            <tr class='title_small'>
                                <For each={P.data.rows[0]}>
                                    {t => (
                                        <th
                                            classList={{
                                                big: BigPaddings.includes(
                                                    t?.toLocaleString() || ''
                                                ),
                                            }}
                                        >
                                            {t.toLocaleString()}
                                        </th>
                                    )}
                                </For>
                            </tr>
                        </thead>
                        <tbody>
                            <For each={P.data.rows.slice(1)}>
                                {tr => (
                                    <tr class='title_smaller'>
                                        <For each={tr}>
                                            {td => (
                                                <td>
                                                    <Show
                                                        when={td}
                                                        fallback={
                                                            <span class='empty-td'>
                                                                خالی!
                                                            </span>
                                                        }
                                                    >
                                                        {td.toLocaleString()}
                                                    </Show>
                                                </td>
                                            )}
                                        </For>
                                    </tr>
                                )}
                            </For>
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }
    return (
        <div
            class='file-sec add-sec'
            classList={{ active: P.data.sec == 'file' }}
        >
            <Uploader />

            <ExcelShower />
        </div>
    )
}

interface ManuSecProps extends SecChildPrps {
    corpPick: boolean
}
export const ManuSec: Component<ManuSecProps> = P => {
    let lastSelected = -1
    let shiftDown = false

    onMount(() => {
        const ac = new AbortController()

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Shift') shiftDown = true
        }
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Shift') shiftDown = false
        }
        const handleBlurOrHide = () => {
            shiftDown = false
        }

        document.addEventListener('keydown', handleKeyDown, {
            signal: ac.signal,
            passive: true,
        })
        document.addEventListener('keyup', handleKeyUp, {
            signal: ac.signal,
            passive: true,
        })
        document.addEventListener(
            'visibilitychange',
            () => {
                if (document.hidden) handleBlurOrHide()
            },
            { signal: ac.signal }
        )

        onCleanup(() => {
            ac.abort()
        })
    })

    const addDef = () => {
        P.setData(
            produce(s => {
                s.helmets?.unshift({
                    ...HELMET_EXTENSION_DEF,
                    id: performance.now(),
                    insurer_phone:
                        DEFAULT_PHONE ?? HELMET_EXTENSION_DEF['insurer_phone'],
                    economic_number:
                        DEFAULT_ECONOMIC_NUMBER ??
                        HELMET_EXTENSION_DEF.economic_number,
                })
            })
        )
    }
    const closeSelect = () => {
        const close = () => {
            P.setData({ select: { show: false, selected: [] } })
        }
        if (P.data.select.selected.length < 10) return close()
        return setPopup({
            show: true,
            title: 'ذخیره نکردید!',
            content: 'بعد از بستن تغییرات شما اعمال نمیشوند.',
            Icon: WarningIcon,
            type: 'warning',
            onSubmit: close,
        })
    }
    const selectAll = () => {
        P.setData(
            produce(s => {
                if (s.select!.selected.length == P.data.helmets.length) {
                    s.select!.selected = []
                } else {
                    s.select!.selected = [...P.data.helmets.map(a => a.id)]
                }
            })
        )
    }
    const showSelect = () => {
        P.setData(produce(s => (s.select!.show = true)))
    }

    const Empty: Component = () => {
        return (
            <div role='button' onclick={addDef} class='empty-helmets title'>
                <LoadingDots />
                <p>برای ادیت دستی، بیمه اضافه کنید.</p>
            </div>
        )
    }

    interface HelmetProps {
        h: HelmetExtension
        index: number
    }
    const Helmet: Component<HelmetProps> = R => {
        function helmet_update(body: Partial<HelmetExtension>) {
            P.setData(
                produce(s => {
                    for (let [k, v] of Object.entries(body)) {
                        // @ts-ignore
                        s.helmets[R.index][k] = v
                    }
                })
            )
        }

        const helData = createMemo(() => P.data.helmets[R.index])

        return (
            <div class='helmet-cmp'>
                <For each={objentries(EXT_MANUALS_LABELS)}>
                    {([k, v]) => {
                        const key = k as keyof HelmetExtension

                        return (
                            <HoverInp
                                holder={v}
                                value={
                                    helData()
                                        ? String(helData()![key] || '')
                                        : ''
                                }
                                onInp={e =>
                                    helmet_update({
                                        [k]:
                                            typeof HELMET_EXTENSION_DEF[k] ==
                                            'number'
                                                ? parseInt(e)
                                                : e,
                                    })
                                }
                                acitve={!!helData()?.[k]}
                                id={`inp-${String(k)}`}
                                inpType={
                                    typeof HELMET_EXTENSION_DEF[k] == 'number'
                                        ? 'number'
                                        : 'text'
                                }
                                Icon={null}
                                format={FORMATS.includes(k)}
                            />
                        )
                    }}
                </For>

                <For each={objentries(HEL_EXT_COL_MAP)}>
                    {([k, { cn }]) => (
                        <HoverInp
                            holder={cn.filter(Boolean)[0]!}
                            value={helData() ? String(helData()![k] || '') : ''}
                            onInp={e =>
                                helmet_update({
                                    [k]:
                                        typeof HELMET_EXTENSION_DEF[k] ==
                                        'number'
                                            ? parseInt(e)
                                            : e,
                                })
                            }
                            acitve={!!helData()?.[k]}
                            id={`inp-${String(k)}`}
                            inpType={
                                typeof HELMET_EXTENSION_DEF[k] == 'number'
                                    ? 'number'
                                    : 'text'
                            }
                            Icon={null}
                            format={FORMATS.includes(k)}
                        />
                    )}
                </For>
            </div>
        )
    }

    interface HelmetWrapped extends HelmetProps {}
    const HelmetWrapped: Component<HelmetWrapped> = W => {
        const [show, setShow] = createSignal(false)

        let bodyRef: HTMLDivElement

        const toggle = () => {
            if (!bodyRef) return

            if (!show()) {
                bodyRef.style.maxHeight = '0px'
                return
            }

            let height = bodyRef.scrollHeight + 64 // paddinng
            bodyRef.style.maxHeight = `${height}px`
        }

        const toggleSelect = () => {
            const wId = W.h.id
            const helmets = P.data.helmets
            const hId = helmets.findIndex(h => h.id === wId)
            if (hId === -1) return
            if (shiftDown && lastSelected >= 0) {
                const start = Math.min(hId, lastSelected)
                const end = Math.max(hId, lastSelected)
                P.setData(
                    produce(s => {
                        const selSet = new Set(s.select?.selected ?? [])
                        let allEnabled = true
                        for (let i = start + 1; i <= end; i++) {
                            if (!selSet.has(helmets[i]!.id)) {
                                allEnabled = false
                                break
                            }
                        }
                        if (allEnabled) {
                            for (let i = start; i <= end; i++)
                                selSet.delete(helmets[i]!.id)
                        } else {
                            for (let i = start; i <= end; i++)
                                selSet.add(helmets[i]!.id)
                        }
                        const newSelected = []
                        for (const h of helmets) {
                            if (selSet.has(h.id)) newSelected.push(h.id)
                        }
                        s.select!.selected = newSelected
                    })
                )
                lastSelected = hId
                return
            }
            lastSelected = hId
            P.setData(
                produce(s => {
                    s.select!.selected ??= []
                    const idx = s.select!.selected.findIndex(id => id === wId)
                    if (idx >= 0) s.select!.selected.splice(idx, 1)
                    else s.select!.selected.push(wId)
                })
            )
        }

        const selected = createMemo(
            () => false
            // P.state.select.selected.includes(W.h.id)
        )

        const delHelmet = () => {
            P.setData(
                produce(s => {
                    s.helmets?.splice(W.index, 1)
                    const idx = s.select!.selected.findIndex(
                        id => id === W.h.id
                    )
                    if (idx >= 0) s.select!.selected.splice(idx, 1)
                })
            )
        }

        const isEmpty = createMemo(() => {
            return (
                !W.h.engine ||
                !W.h.vin ||
                !W.h.chassis ||
                !W.h.insurer ||
                !W.h.plate_number ||
                !W.h.plate_serial ||
                !W.h.manufacture_year
            )
        })

        return (
            <div class='helmet-wrapped-container'>
                <button
                    type='button'
                    class='wrapped-select'
                    onclick={toggleSelect}
                    classList={{
                        active: P.data.select.selected.includes(W.h.id),
                    }}
                >
                    <div class='icon fill'>
                        <CheckboxFill />
                    </div>
                    <div class='icon unfill'>
                        <CheckboxUnFill />
                    </div>
                </button>
                <div
                    class='helmet-wrapped'
                    classList={{
                        active: show(),
                        green: !isEmpty(),
                        selected: selected(),
                    }}
                >
                    <div
                        class='wrapped-head title_small'
                        role='button'
                        onclick={() => {
                            setShow(v => !v)
                            toggle()
                        }}
                    >
                        <div class='holder-wrapper'>
                            <div
                                class='cta icon'
                                onclick={e => {
                                    e.stopPropagation()
                                    e.preventDefault()

                                    delHelmet()
                                }}
                            >
                                <DeleteIcon />
                            </div>
                            <div class='holder'>
                                بیمه شماره {W.index + 1}{' '}
                                {isEmpty() && '(خالی)'}{' '}
                            </div>
                        </div>
                        <div class='arr icon'>
                            <ArrowDownIcon />
                        </div>
                    </div>
                    <div class='wrapped-body' ref={e => (bodyRef = e)}>
                        <Helmet h={W.h} index={W.index} />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            class='manu-sec add-sec'
            classList={{
                active: P.data.sec == 'manual' || P.add.type == 'edit',
                editing: P.add.type == 'edit',
            }}
        >
            <Show when={P.add.type == 'add'}>
                <div class='add-cta-wrapper title_smaller'>
                    <div
                        class='save-info'
                        classList={{
                            active: P.data.select.selected.length !== 0,
                        }}
                    >
                        <div
                            class='close-save icon'
                            role='button'
                            onclick={closeSelect}
                        >
                            <CloseIcon />
                        </div>
                        <button
                            type='button'
                            class='setting-icon icon'
                            onclick={showSelect}
                        >
                            <SettingFillIcon />
                        </button>
                        <div class='title'>{P.data.select.selected.length}</div>
                        <div class='icon' role='button' onclick={selectAll}>
                            <SelectAllIcon />
                        </div>
                    </div>
                    <AddBtn title='اضافه دستی' onClick={addDef} />
                </div>
            </Show>
            <div class='helmets-wrapper'>
                <Show when={P.data.helmets.length !== 0} fallback={<Empty />}>
                    <Show
                        when={P.add.type == 'add'}
                        fallback={
                            <Helmet
                                index={0}
                                h={deepcopy(P.data.helmets[0]!)}
                            />
                        }
                    >
                        <For each={P.data.helmets.slice(0, 30)}>
                            {(h, idx) => (
                                <HelmetWrapped index={idx()} h={deepcopy(h)} />
                            )}
                        </For>
                    </Show>
                </Show>
            </div>
        </div>
    )
}

export default ExtentionSubmitteds
